
export default {
  name: "DanfeNfce",

  props: {
    visivel: { type: Boolean, default: false },
    uuid:    { type: String,  default: null  },
  },

  emits: ["fechar"],

  data() {
    return {
      carregando:    false,
      erro:          null,
      nfce:          null,
      emitente:      {},
      configuracao:  {},
      qrCodeUrl:     null,
      htmlDanfePadrao: "",
    };
  },

  computed: {
    itens() {
      return this.nfce?.itens ?? [];
    },
    pagamentos() {
      return this.nfce?.pagamentos ?? [];
    },
    totalDesconto() {
      return this.itens.reduce((s, i) => s + Number(i.desconto ?? 0), 0);
    },
    troco() {
      const pago   = this.pagamentos.reduce((s, p) => s + Number(p.valor ?? 0), 0);
      const total  = Number(this.nfce?.valorTotal ?? 0);
      const diff   = pago - total;
      return diff > 0.005 ? diff : 0;
    },
    statusFiscal() {
      return this.nfce?.status_fiscal ?? null;
    },
    eAutorizada() {
      return this.statusFiscal === "autorizada" && !!this.nfce?.protocolo;
    },
    eContingencia() {
      return !this.eAutorizada &&
             this.statusFiscal !== "rejeitada" &&
             !!this.nfce?.xml_assinado;
    },
    podeImprimir() {
      if (this.statusFiscal === "rejeitada") return false;
      return this.eAutorizada || !!this.nfce?.xml_assinado;
    },
    isHomolog() {
      return parseInt(this.configuracao?.ambiente ?? 2, 10) === 2;
    },
    logoUrl() {
      const p = this.emitente?.payload_json;
      if (!p) return null;
      try {
        const obj = typeof p === "string" ? JSON.parse(p) : p;
        return obj?.logo_url ?? obj?.logo ?? null;
      } catch { return null; }
    },
    urlConsulta() {
      return this._extrairUrlConsulta();
    },
  },

  watch: {
    visivel(val) {
      if (val && this.uuid) this.carregarDados();
      else if (!val) this.limpar();
    },
    uuid(val) {
      if (val && this.visivel) this.carregarDados();
    },
  },

  methods: {
    async carregarDados() {
      this.carregando = true;
      this.erro       = null;
      this.nfce       = null;
      this.emitente   = {};
      this.qrCodeUrl  = null;
      this.htmlDanfePadrao = "";

      try {
        const api = this.$electron;
        if (!api) throw new Error("Electron API não disponível.");

        const [dadosNfce, dadosEmitente, dadosConfig] = await Promise.all([
          api.nfce.getByUuid(this.uuid),
          api.emitente.buscarEmitente(),
          api.configuracaoNfce?.buscarConfiguracao?.() ?? Promise.resolve({}),
        ]);

        if (!dadosNfce) throw new Error("NFC-e não encontrada.");

        this.nfce         = dadosNfce;
        this.emitente     = dadosEmitente ?? {};
        this.configuracao = dadosConfig   ?? {};
        this.qrCodeUrl    = this._extrairQrCode(dadosNfce);

        try {
          const danfeRet = await api.nfce.gerarDanfeHtml(this.uuid);
          this.htmlDanfePadrao = (danfeRet && danfeRet.html) || "";
        } catch (_) {
          this.htmlDanfePadrao = "";
        }
      } catch (err) {
        this.erro = err.message || String(err);
      } finally {
        this.carregando = false;
      }
    },

    _extrairQrCode(nfce) {
      // Prioridade: xml_autorizado → xml_assinado → xml
      const xml = nfce.xml_autorizado || nfce.xml_assinado || nfce.xml || "";
      if (xml) {
        // Suporta <qrCode>URL</qrCode> e <qrCode><![CDATA[URL]]></qrCode>
        const m = xml.match(/<qrCode>(?:<!\[CDATA\[)?([\s\S]+?)(?:\]\]>)?<\/qrCode>/i);
        if (m?.[1]) {
          return m[1]
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .trim();
        }
      }
      return nfce.url_qr_code ?? null;
    },

    _extrairUrlConsulta() {
      if (!this.nfce) return null;
      const xml = this.nfce.xml_autorizado || this.nfce.xml_assinado || this.nfce.xml || "";
      if (xml) {
        const m = xml.match(/<urlChave>(?:<!\[CDATA\[)?([\s\S]+?)(?:\]\]>)?<\/urlChave>/i);
        if (m?.[1]) return m[1].replace(/&amp;/g, "&").trim();
      }
      // Fallback por UF
      const uf   = (this.emitente.uf || "").toUpperCase();
      const homolog = this.isHomolog;
      const urls = {
        SC: homolog ? "https://hom.sat.sef.sc.gov.br/nfce/consulta" : "https://sat.sef.sc.gov.br/nfce/consulta",
        SP: homolog ? "https://homologacao.nfce.fazenda.sp.gov.br/NFCeConsultaPublica" : "https://www.nfce.fazenda.sp.gov.br/NFCeConsultaPublica",
        PR: "https://www.fazenda.pr.gov.br/nfce/",
        RS: "https://www.sefaz.rs.gov.br/NFCE/",
        MG: "https://nfce.fazenda.mg.gov.br/",
        RJ: "https://www.nfce.fazenda.rj.gov.br/",
        GO: "https://nfce.sefaz.go.gov.br/",
        BA: "https://www.sefaz.ba.gov.br/nfce/",
        PE: "https://nfce.sefaz.pe.gov.br/",
        CE: "https://nfce.sefaz.ce.gov.br/",
        MT: "https://www.sefaz.mt.gov.br/-nfce/",
        MS: "https://www.dfe.ms.gov.br/",
        PA: "https://www.sefa.pa.gov.br/nfce/",
        AM: "https://sistemas.sefaz.am.gov.br/nfce/",
        ES: "https://app.sefaz.es.gov.br/ConsultaNFCe",
        DF: "https://dec.fazenda.df.gov.br/ConsultarNFCe.aspx",
        RN: "https://nfce.set.rn.gov.br/",
        PB: "https://www.receita.pb.gov.br/nfce/",
        AL: "https://www.sefaz.al.gov.br/nfce/",
        PI: "https://nfce.sefaz.pi.gov.br/",
        SE: "https://nfce.se.gov.br/",
        MA: "https://www.sefaz.ma.gov.br/nfce/",
        TO: "https://sefaz.to.gov.br/nfce/",
        RO: "https://www.sefin.ro.gov.br/nfce/",
        RR: "https://portal.sefaz.rr.gov.br/nfce/",
        AP: "https://www.sefaz.ap.gov.br/nfce/",
        AC: "https://sat.sefa.ac.gov.br/p/nfce/",
      };
      return urls[uf] ?? null;
    },

    async imprimir() {
      if (!this.podeImprimir || !this.uuid) return;
      try {
        const api = this.$electron;
        if (!api?.printer?.imprimirDanfeNfce) {
          throw new Error("Serviço de impressão não disponível.");
        }
        await api.printer.imprimirDanfeNfce({ uuid_venda_terminal: this.uuid });
      } catch (e) {
        this.erro = e?.message || "Erro ao enviar DANFE para impressão.";
      }
    },

    fechar() {
      this.$emit("fechar");
    },

    limpar() {
      this.nfce         = null;
      this.emitente     = {};
      this.configuracao = {};
      this.qrCodeUrl    = null;
      this.htmlDanfePadrao = "";
      this.erro         = null;
    },

    fmtN(v) {
      return Number(v ?? 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatQtd(v) {
      const n = Number(v ?? 0);
      return n % 1 === 0
        ? String(n)
        : n.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    },

    formatarDH(dt) {
      if (!dt) return "—";
      try {
        return new Date(dt).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
      } catch { return String(dt); }
    },

    formatCnpj(v) {
      if (!v) return "—";
      const s = String(v).replace(/\D/g, "");
      if (s.length !== 14) return v;
      return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    },

    formatCpfCnpj(v) {
      if (!v) return "—";
      const s = String(v).replace(/\D/g, "");
      if (s.length === 11) return s.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
      if (s.length === 14) return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
      return v;
    },

    formatChave(v) {
      if (!v) return "—";
      return String(v).replace(/(.{4})(?=.)/g, "$1 ").trim();
    },
  },
};
